import { lazy, Suspense, useEffect } from 'react'
import { Navigate, Outlet, Route, Routes, useLocation, useSearchParams } from 'react-router-dom'
import * as Sentry from '@sentry/react'

import { SSOSignIn } from '../app/Auth/SSOSignIn/SSOSignIn'
import { EmailSignIn } from '../app/Auth/EmailSignIn/EmailSignIn'
import { SSOGateway } from '../app/Auth/SSOGateway/SSOGateway'
import { ForgotPassword } from '../app/Auth/ForgotPassword/ForgotPassword'
import { SignWithTwoFA } from '../app/Auth/TwoFactorAuthentication/SignInWithTwoFA/SignWithTwoFA'
import { permission, scope, withAuth } from '../common/hoc/withPermissions'
import { getAccountFromAuthState } from '../context/auth/authContextController/AuthContextController'
import { useAnalytics } from '../common/hooks/useAnalytics/useAnalytics'
import { getPageTrackingName } from '../utils/getPageTrackingName/getPageTrackingName'
import { getQueryParamsObjWithoutToken } from '../utils/getQueryParamsObjWithoutToken'
import { NavBar } from '../common/components'
import { Home } from '../app/Home/Home'
import { EditVolunteeringActivity } from '../app/Volunteer/LogVolunteeringActivity/Edit/EditVolunteeringActivity'
import { GlobalSearchPage } from '../app/SearchPages/GlobalSearchPage/GlobalSearchPage'
import { OrganisationsSearchPage } from '../app/SearchPages/OrganisationsSearchPage/OrganisationsSearchPage'
import { FundraisersSearchPage } from '../app/SearchPages/FundraisersSearchPage/FundraisersSearchPage'
import { GivingActionsSearchPage } from '../app/SearchPages/GivingActionsSearchPage/GivingActionsSearchPage'
import { VolunteeringSearchPage } from '../app/SearchPages/VolunteeringSearchPage/VolunteeringSearchPage'
import { Campaign } from '../app/Campaign/Campaign'
import { Permissions } from '../constants/permissions'
import { ProfileCreation } from '../app/Onboarding/NewOnboarding/ProfileCreation/ProfileCreation'
import { PlatformTour } from '../app/Onboarding/NewOnboarding/PlatformTour/PlatformTour'
import { OnboardingSuccess } from '../app/Onboarding/NewOnboarding/OnboardingSuccess/OnboardingSuccess'
import { MyCampaigns } from '../app/MyCampaigns/MyCampaigns'
import { MyAchievements } from '../app/Gamification/MyAchievements/MyAchievements'
import { Settings } from '../app/Settings/Settings'
import { SessionBridge } from '../app/SessionBridge/SessionBridge'
import { RequestNonprofitWizard } from '../app/RequestNonprofit/RequestNonprofitWizard'
import { AdyenRedirectPage } from '../app/AdyenRedirect/AdyenRedirectPage'

import { AllowAuthenticatedAccountOnlyRoute } from './AllowAuthenticatedAccountOnlyRoute'
import { AllowOnboardedAccountOnlyRoute } from './AllowOnboardedAccountOnlyRoute'
import { AppRoute } from './AppRoute.enum'
import { AllowUnauthenticatedAccountOnlyRoute } from './AllowUnauthenticatedAccountOnlyRoute'
import { useAuth } from '@percent/workplace-giving/common/hooks'
import { usePostHogFeatureFlag } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/usePostHogFeatureFlag'
import { POSTHOG_FEATURE_FLAGS } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'
import { HandleNotFoundRedirection } from './HandleNotFoundRedirection'
import { Loader } from '@percent/lemonade'

const Donate = lazy(() => import('../app/Donate/Donate').then(module => ({ default: module.Donate })))
const Fundraiser = lazy(() => import('../app/Fundraiser/Fundraiser').then(module => ({ default: module.Fundraiser })))
const CreateOpportunity = lazy(() =>
  import('../app/Volunteer/OpportunityForm/Create/CreateOpportunity').then(module => ({
    default: module.CreateOpportunity
  }))
)
const EditOpportunity = lazy(() =>
  import('../app/Volunteer/OpportunityForm/Edit/EditOpportunity').then(module => ({ default: module.EditOpportunity }))
)
const EditSurvey = lazy(() =>
  import('../app/Volunteer/OpportunityForm/Edit/EditSurvey').then(module => ({ default: module.EditSurvey }))
)
const MyFundraisers = lazy(() =>
  import('../app/Fundraiser/MyFundraisers/MyFundraisers').then(module => ({ default: module.MyFundraisers }))
)
const MyProfile = lazy(() => import('../app/MyProfile/MyProfile').then(module => ({ default: module.MyProfile })))
const FundraiserWizard = lazy(() =>
  import('../app/Fundraiser/FundraiserWizard/FundraiserWizard').then(module => ({ default: module.FundraiserWizard }))
)
const EditFundraiserWizard = lazy(() =>
  import('../app/Fundraiser/FundraiserWizard/Edit/EditFundraiserWizard').then(module => ({
    default: module.EditFundraiserWizard
  }))
)
const DonationMatchRequestWizard = lazy(() =>
  import('../app/DonationMatchRequest/DonationMatchRequestWizard').then(module => ({
    default: module.DonationMatchRequestWizard
  }))
)
const LogVolunteeringActivity = lazy(() =>
  import('../app/Volunteer/LogVolunteeringActivity/LogVolunteeringActivity').then(module => ({
    default: module.LogVolunteeringActivity
  }))
)
const MyImpact = lazy(() => import('../app/MyImpact/MyImpact').then(module => ({ default: module.MyImpact })))
const Volunteer = lazy(() => import('../app/Volunteer/Volunteer').then(module => ({ default: module.Volunteer })))
const MyOpportunities = lazy(() =>
  import('../app/MyOpportunities/MyOpportunities').then(module => ({ default: module.MyOpportunities }))
)
const OpportunityDetails = lazy(() =>
  import('../app/Volunteer/OpportunityDetails/OpportunityDetails').then(module => ({
    default: module.OpportunityDetails
  }))
)

const CreateCampaign = lazy(() =>
  import('../app/CampaignForm/Create/CreateCampaign').then(module => ({
    default: module.CreateCampaign
  }))
)

const EditCampaign = lazy(() =>
  import('../app/CampaignForm/Edit/EditCampaign').then(module => ({
    default: module.EditCampaign
  }))
)

const EditAttendance = lazy(() =>
  import('../app/Volunteer/EditAttendance/EditAttendance').then(module => ({
    default: module.EditAttendance
  }))
)
const SurveyResponse = lazy(() =>
  import('../app/Volunteer/SurveyResponse/SurveyResponse').then(module => ({
    default: module.SurveyResponse
  }))
)

const AiSearchPage = lazy(() =>
  import('../app/AiSearch/AiSearchPage').then(module => ({ default: module.AiSearchPage }))
)

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

function NavigateHome() {
  return <Navigate to={AppRoute.HOME} />
}

const AllowFundraising = withAuth(scope('donation_link'))(Outlet, NavigateHome)
const AllowVolunteering = withAuth(scope('volunteering'))(Outlet, NavigateHome)
const AllowCreateCampaign = withAuth(permission(Permissions.CampaignsWrite))(Outlet, NavigateHome)
const AllowGivingAi = withAuth(scope('giving_ai'))(Outlet, NavigateHome)

export function AppRoutes() {
  const { state } = useAuth()
  const user = getAccountFromAuthState(state)
  const { page } = useAnalytics()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const aiSearchEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.WPG_AI_SEARCH)
  const givingActionsEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.WPG_GIVING_ACTIONS)

  useEffect(() => {
    const authorizedPageName = getPageTrackingName(pathname)

    if (user?.id && authorizedPageName) {
      page({
        name: authorizedPageName,
        category: 'WPG',
        properties: {
          ...getQueryParamsObjWithoutToken(searchParams)
        }
      })
    }
  }, [pathname, searchParams, page, user?.id])

  return (
    <Suspense
      fallback={
        <>
          <NavBar />
          <Loader />
        </>
      }
    >
      <SentryRoutes>
        <Route path={AppRoute.SESSION_BRIDGE} element={<SessionBridge />} />

        <Route element={<AllowUnauthenticatedAccountOnlyRoute />}>
          <Route path={AppRoute.SIGN_IN} element={<EmailSignIn />} />
          <Route path={AppRoute.OTP_VERIFY} element={<SignWithTwoFA />} />
          <Route path={AppRoute.SSO_SIGN_IN} element={<SSOSignIn />} />
          <Route path={AppRoute.FORGOT_PASSWORD} element={<ForgotPassword />} />
        </Route>

        <Route element={<AllowAuthenticatedAccountOnlyRoute />}>
          <Route path={AppRoute.SSO_GATEWAY} element={<SSOGateway />} />
        </Route>

        {/* without layout TODO: Change this to something more elegant */}
        <Route element={<AllowAuthenticatedAccountOnlyRoute withoutLayout />}>
          <Route path={AppRoute.NEW_ONBOARDING} element={<ProfileCreation />} />
          <Route path={AppRoute.PLATFORM_TOUR} element={<PlatformTour />} />
          <Route path={AppRoute.ONBOARDING_SUCCESS} element={<OnboardingSuccess />} />
        </Route>

        <Route element={<AllowOnboardedAccountOnlyRoute />}>
          <Route path={AppRoute.HOME} element={<Home />} />
          <Route
            path={AppRoute.HOME_ADYEN_REDIRECT}
            element={
              <AdyenRedirectPage>
                <Home />
              </AdyenRedirectPage>
            }
          />
          <Route path={AppRoute.SEARCH} element={<GlobalSearchPage />} />
          <Route
            path={AppRoute.SEARCH_ADYEN_REDIRECT}
            element={
              <AdyenRedirectPage>
                <GlobalSearchPage />
              </AdyenRedirectPage>
            }
          />
          <Route path={AppRoute.ORGANISATIONS} element={<OrganisationsSearchPage />} />
          <Route
            path={AppRoute.ORGANISATIONS_ADYEN_REDIRECT}
            element={
              <AdyenRedirectPage>
                <OrganisationsSearchPage />
              </AdyenRedirectPage>
            }
          />
          <Route path={AppRoute.FUNDRAISERS} element={<FundraisersSearchPage />} />
          <Route path={AppRoute.VOLUNTEERING} element={<VolunteeringSearchPage />} />
          {givingActionsEnabled && <Route path={AppRoute.GIVING_ACTIONS} element={<GivingActionsSearchPage />} />}
          <Route
            path={AppRoute.ADYEN_REDIRECT}
            element={
              <AdyenRedirectPage>
                <Donate />
              </AdyenRedirectPage>
            }
          />
          <Route path={AppRoute.DONATE} element={<Donate />} />
          <Route path={AppRoute.MY_IMPACT} element={<MyImpact />} />
          <Route path={AppRoute.CAMPAIGN} element={<Campaign />} />
          <Route
            path={AppRoute.CAMPAIGN_ADYEN_REDIRECT}
            element={
              <AdyenRedirectPage>
                <Campaign />
              </AdyenRedirectPage>
            }
          />
          <Route path={AppRoute.SETTINGS} element={<Settings />} />
          {aiSearchEnabled && (
            <Route element={<AllowGivingAi />}>
              <Route path={AppRoute.AI_SEARCH} element={<AiSearchPage />} />
              <Route path={AppRoute.AI_SEARCH_THREAD} element={<AiSearchPage />} />
            </Route>
          )}

          <Route element={<AllowFundraising />}>
            <Route path={AppRoute.FUNDRAISER} element={<Fundraiser />} />
            <Route
              path={AppRoute.FUNDRAISER_ADYEN_REDIRECT}
              element={
                <AdyenRedirectPage>
                  <Fundraiser />
                </AdyenRedirectPage>
              }
            />
            <Route
              path={AppRoute.MY_FUNDRAISER}
              element={
                <MyProfile>
                  <MyFundraisers />
                </MyProfile>
              }
            />
          </Route>

          <Route element={<AllowVolunteering />}>
            <Route path={AppRoute.VOLUNTEER} element={<Volunteer />} />
            <Route path={AppRoute.OPPORTUNITY_DETAILS} element={<OpportunityDetails />} />
            <Route
              path={AppRoute.MY_OPPORTUNITY}
              element={
                <MyProfile>
                  <MyOpportunities />
                </MyProfile>
              }
            />
          </Route>

          <Route
            path={AppRoute.MY_CAMPAIGNS}
            element={
              <MyProfile>
                <MyCampaigns />
              </MyProfile>
            }
          />

          <Route
            path={AppRoute.MY_ACHIEVEMENTS}
            element={
              <MyProfile>
                <MyAchievements />
              </MyProfile>
            }
          />
        </Route>

        {/* without layout TODO: Change this to something more elegant */}
        <Route element={<AllowOnboardedAccountOnlyRoute withoutLayout />}>
          <Route element={<AllowVolunteering />}>
            <Route key="create-opportunity" path={AppRoute.CREATE_OPPORTUNITY} element={<CreateOpportunity />} />
            <Route key="edit-opportunity" path={AppRoute.EDIT_OPPORTUNITY} element={<EditOpportunity />} />
            <Route key="edit-survey" path={AppRoute.EDIT_SURVEY} element={<EditSurvey />} />
            <Route
              key="log-volunteering-activity"
              path={AppRoute.LOG_VOLUNTEERING_ACTIVITY}
              element={<LogVolunteeringActivity />}
            />
            <Route
              key="edit-volunteering-activity"
              path={AppRoute.EDIT_VOLUNTEERING_ACTIVITY}
              element={<EditVolunteeringActivity />}
            />
            <Route key="edit-attendance" path={AppRoute.EDIT_ATTENDANCE} element={<EditAttendance />} />
            <Route key="survey-response" path={AppRoute.SURVEY_RESPONSE} element={<SurveyResponse />} />
          </Route>
          <Route element={<AllowFundraising />}>
            <Route key="create-fundraiser" path={AppRoute.CREATE_FUNDRAISER} element={<FundraiserWizard />} />
            <Route key="edit-fundraiser" path={AppRoute.EDIT_FUNDRAISER} element={<EditFundraiserWizard />} />
          </Route>
          <Route element={<AllowCreateCampaign />}>
            <Route key="create-campaign" path={AppRoute.CREATE_CAMPAIGN} element={<CreateCampaign />} />
            <Route key="edit-campaign" path={AppRoute.EDIT_CAMPAIGN} element={<EditCampaign />} />
          </Route>
          <Route path={AppRoute.REQUEST_NONPROFIT} element={<RequestNonprofitWizard />} />
          <Route
            key="create-match-request"
            path={AppRoute.CREATE_MATCH_REQUEST}
            element={<DonationMatchRequestWizard />}
          />
        </Route>

        <Route path="*" element={<HandleNotFoundRedirection />} />
      </SentryRoutes>
    </Suspense>
  )
}
